import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, TrendingDown, Timer, Pill, Activity } from "lucide-react";

/** Practice Insights — small card for the Dashboard.
 * Shows: last-7-days appointments (bar chart + trend vs prior 7 days),
 * average consultation time, and top prescribed medications. */
export default function PracticeInsights() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/insights")
      .then((r) => { if (mounted) setD(r.data); })
      .catch(() => { if (mounted) setErr(true); });
    return () => { mounted = false; };
  }, []);

  if (err) return null;

  const series = d?.weekly_series || [];
  const maxCount = Math.max(1, ...series.map((s) => s.count || 0));
  const total = d?.weekly_total ?? 0;
  const trend = d?.trend_pct ?? 0;
  const trendUp = trend >= 0;
  const avgSec = d?.avg_consultation_seconds;
  const avgLabel = avgSec == null
    ? "—"
    : avgSec >= 60
      ? `${Math.round(avgSec / 60)}m ${avgSec % 60}s`
      : `${avgSec}s`;
  const meds = d?.top_medications || [];
  const dayLabel = (iso) => {
    if (!iso) return "";
    const dt = new Date(iso + "T00:00:00");
    return dt.toLocaleDateString(undefined, { weekday: "short" }).slice(0, 1);
  };

  return (
    <div className="card-soft p-6 md:p-8 mb-8" data-testid="practice-insights">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="field-label text-[#0A4C36]">Practice Insights</div>
          <div className="text-lg font-semibold text-[#1F2A24] mt-1" style={{ fontFamily: "Instrument Sans" }}>
            Last 7 days at a glance
          </div>
        </div>
        <Activity size={18} className="text-[#0A4C36]/70" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Weekly appointments */}
        <div className="rounded-2xl bg-[#F7F4EC] p-5" data-testid="insight-weekly">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-[#6C7B72]">Appointments</div>
            <div className={`inline-flex items-center gap-1 text-xs font-semibold ${trendUp ? "text-[#0A4C36]" : "text-[#B14A2A]"}`}>
              {trendUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {trend > 0 ? "+" : ""}{trend}%
            </div>
          </div>
          <div className="text-3xl font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }} data-testid="insight-weekly-total">
            {total}
          </div>
          <div className="text-xs text-[#8A9992] mt-1">vs {d?.weekly_prev_total ?? 0} prior week</div>
          <div className="mt-4 flex items-end gap-1.5 h-14">
            {series.map((s, i) => (
              <div key={s.date} className="flex-1 flex flex-col items-center gap-1" title={`${s.date}: ${s.count}`}>
                <div
                  className={`w-full rounded-t-md ${s.count > 0 ? "bg-[#0A4C36]" : "bg-[#0A4C36]/15"}`}
                  style={{ height: `${Math.max(6, (s.count / maxCount) * 100)}%` }}
                />
                <div className="text-[10px] text-[#8A9992]">{dayLabel(s.date)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Avg consultation time */}
        <div className="rounded-2xl bg-[#FCE7D9]/60 p-5" data-testid="insight-avg-time">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-[#8A5A34]">Avg consult time</div>
            <Timer size={14} className="text-[#8A5A34]" />
          </div>
          <div className="text-3xl font-semibold text-[#4A2C13]" style={{ fontFamily: "Instrument Sans" }} data-testid="insight-avg-value">
            {avgLabel}
          </div>
          <div className="text-xs text-[#5C3A21]/80 mt-1">
            {d?.avg_consultation_samples > 0
              ? `across ${d.avg_consultation_samples} recent consultation${d.avg_consultation_samples === 1 ? "" : "s"}`
              : "start saving a consultation to track"}
          </div>
          <div className="mt-4 text-xs text-[#5C3A21]/70">
            Target: keep it under <b>2 min</b> — spend more time with the patient.
          </div>
        </div>

        {/* Top medications */}
        <div className="rounded-2xl bg-[#E6F0EC] p-5" data-testid="insight-top-meds">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-[#0A4C36]">Top prescribed (30d)</div>
            <Pill size={14} className="text-[#0A4C36]" />
          </div>
          {meds.length === 0 ? (
            <div className="text-sm text-[#5C7568]">No prescriptions yet — save a consultation with medications to populate.</div>
          ) : (
            <ol className="space-y-2">
              {meds.map((m, i) => (
                <li key={m.name} className="flex items-center justify-between text-sm" data-testid={`insight-med-${i}`}>
                  <span className="flex items-center gap-2 text-[#1F2A24]">
                    <span className="w-5 h-5 rounded-full bg-[#0A4C36] text-white text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
                    <span className="truncate">{m.name}</span>
                  </span>
                  <span className="text-[#0A4C36] font-semibold">{m.count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
